/* eslint-disable */
import { Menu, Transition } from '@headlessui/react'
import axios from 'axios'
import { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Modal from '../../components/UI/Modal/Modal'
import Pagination from '../../components/UI/Table/Pagination'
import Table from '../../components/UI/Table/Table'
import CreateScheduleNotification from './CreateScheduleNotification'

export interface Notification {
  id: string
  advertId: string
  heading: string
  subHeading: string
  launchTrigger: string
  notificationSendTime: string | null
  notificationSendTimeZone: string | null
  notificationSendTimeInUTC: string | null
  timeAfterSavingUnit: string | null
  timeAfterSavingValue: number | null
  timeAfterSavingExpiry: string | null
  timeAfterSavingExpiryTimeZone: string | null
  timeAfterSavingExpiryInUTC: string | null
  creationTime: string
  lastModifiedTime: string
  isSent: boolean
  sentTime: string | null
  sendOnAppleWallet: boolean
  sendOnGoogleWallet: boolean
  status: string
  failureReason: string | null
  retryCount: number
  nextRetryTime: string | null
}

const PAGE_SIZE = 20

const PassNotifications = () => {
  const { adId } = useParams<{ adId: string }>()
  const [tab, setTab] = useState<'Scheduled' | 'Expired'>('Scheduled')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshFlag, setRefreshFlag] = useState(0)
  const [editNotification, setEditNotification] = useState<Notification | null>(null)
  const [viewOnly, setViewOnly] = useState(false)
  const [adTitle, setAdTitle] = useState<string>('')
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmAction, setConfirmAction] = useState<'delete' | 'cancel' | null>(null)
  const [actionNotification, setActionNotification] = useState<Notification | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    if (!adId) return
    setLoading(true)
    setError(null)
    const baseUrl = process.env.REACT_APP_GATEWAY_URL || ''
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${baseUrl}/wallet-notifications/${adId}`, {
          headers: {
            Accept: 'application/json',
          },
        })
        // Extract advertisement title if present
        if (res.data && res.data.advertisement && res.data.advertisement.title) {
          setAdTitle(res.data.advertisement.title)
        } else {
          setAdTitle('')
        }
        // Map API data to table format
        const mapped = (res.data.notifications || []).map((n: any) => ({
          id: n.id,
          advertId: n.advertId,
          heading: n.heading,
          subHeading: n.subHeading,
          launchTrigger: n.launchTrigger,
          notificationSendTime: n.notificationSendTime,
          notificationSendTimeZone: n.notificationSendTimeZone,
          notificationSendTimeInUTC: n.notificationSendTimeInUTC,
          timeAfterSavingUnit: n.timeAfterSavingUnit,
          timeAfterSavingValue: n.timeAfterSavingValue,
          timeAfterSavingExpiry: n.timeAfterSavingExpiry,
          timeAfterSavingExpiryTimeZone: n.timeAfterSavingExpiryTimeZone,
          timeAfterSavingExpiryInUTC: n.timeAfterSavingExpiryInUTC,
          creationTime: n.creationTime,
          lastModifiedTime: n.lastModifiedTime,
          isSent: n.isSent,
          sentTime: n.sentTime,
          sendOnAppleWallet: n.sendOnAppleWallet,
          sendOnGoogleWallet: n.sendOnGoogleWallet,
          status: n.status,
          failureReason: n.failureReason,
          retryCount: n.retryCount,
          nextRetryTime: n.nextRetryTime,
        }))
        setNotifications(mapped)
      } catch (err) {
        setError('Failed to load notifications')
      } finally {
        setLoading(false)
      }
    }
    fetchNotifications()
  }, [adId, refreshFlag])

  // Filter and paginate notifications
  const filtered = notifications.filter(
    n =>
      // Exclude DELETED notifications from both tabs
      n.status?.toUpperCase() !== 'DELETED' &&
      (tab === 'Expired' ? n.status?.toUpperCase() === 'EXPIRED' : n.status?.toUpperCase() !== 'EXPIRED') &&
      (n.heading.toLowerCase().includes(search.toLowerCase()) ||
        n.subHeading.toLowerCase().includes(search.toLowerCase()) ||
        n.launchTrigger.toLowerCase().includes(search.toLowerCase()))
  )
  const total = filtered.length
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleAction = async () => {
    if (!actionNotification || !confirmAction) return
    setActionLoading(true)
    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_URL || ''}/wallet-notifications/${adId}`,
        {
          walletNotificationId: actionNotification.id,
          notificationStatus: confirmAction === 'delete' ? 'DELETED' : 'EXPIRED',
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      setRefreshFlag(f => f + 1)
      setConfirmModalOpen(false)
      setActionNotification(null)
      setConfirmAction(null)
    } catch (err) {
      alert('Failed to update notification status.')
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="p-6 bg-gray-100 min-h-[90vh] w-full">
      <div className="max-w-5xl mx-auto bg-white p-8 rounded-lg shadow flex flex-col h-[80vh]">
        <button
          type="button"
          className="text-blue-600 flex items-center mb-6 font-medium hover:underline"
          onClick={() => window.history.back()}
        >
          <span className="mr-2">&larr;</span> Back to Adverts
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <h2 className="text-2xl font-bold">{adTitle || 'Advertisement'}</h2>
          <div className="flex gap-2 w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by Heading, Sub Heading, etc"
              className="border rounded px-4 py-2 w-full md:w-72 focus:outline-none focus:ring"
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
            <button
              type="button"
              className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-700 flex items-center"
              onClick={() => setShowScheduleModal(true)}
            >
              Schedule Notification
            </button>
          </div>
        </div>
        <div className="flex border-b mb-4">
          <button
            type="button"
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              tab === 'Scheduled' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
            }`}
            onClick={() => {
              setTab('Scheduled')
              setPage(1)
            }}
          >
            Scheduled
          </button>
          <button
            type="button"
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              tab === 'Expired' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
            }`}
            onClick={() => {
              setTab('Expired')
              setPage(1)
            }}
          >
            Expired
          </button>
        </div>
        <div className="flex flex-col flex-grow min-h-0 overflow-hidden border rounded-lg shadow-sm bg-white">
          <div className="flex-grow overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-blue-600 text-lg font-semibold">Loading...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-red-600 text-lg font-semibold">{error}</span>
              </div>
            ) : (
              <Table className="flex-grow" dataCount={paginated.length}>
                <Table.Thead>
                  <Table.Tr className="bg-gray-50 sticky top-0 z-10 border-b">
                    <Table.Th className="py-3">ID</Table.Th>
                    <Table.Th className="py-3">Headline</Table.Th>
                    <Table.Th className="py-3">Sub-Heading</Table.Th>
                    <Table.Th className="py-3">Launch Trigger</Table.Th>
                    <Table.Th className="py-3">Expiry (Timezone)</Table.Th>
                    <Table.Th className="py-3">UTC Expiry</Table.Th>
                    <Table.Th className="py-3">Status</Table.Th>
                    <Table.Th className="py-3">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginated.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={8} className="text-center py-6 text-gray-400">
                        No notifications found.
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    paginated.map((n, idx) => {
                      // Helper to show N/A for null/empty
                      const display = (val: any) => (val === null || val === undefined || val === '' ? 'N/A' : val)
                      // Launch Trigger display
                      let launchTriggerDisplay =
                        n.launchTrigger === 'TIME_AFTER_SAVING'
                          ? n.timeAfterSavingUnit === 'INSTANT' && n.timeAfterSavingValue === 0
                            ? 'Just After Saving'
                            : n.timeAfterSavingValue && n.timeAfterSavingUnit
                            ? `${n.timeAfterSavingValue} ${n.timeAfterSavingUnit.toLowerCase()} after saving`
                            : 'N/A'
                          : n.launchTrigger === 'SPECIFIC_DATE_AND_TIME' &&
                            n.notificationSendTime &&
                            n.notificationSendTimeZone
                          ? `${new Date(n.notificationSendTime).toUTCString()}, ${n.notificationSendTimeZone}`
                          : 'N/A'
                      // Expiry and Timezone combined
                      let expiryDisplay =
                        n.launchTrigger === 'TIME_AFTER_SAVING' && n.timeAfterSavingExpiry
                          ? `${new Date(n.timeAfterSavingExpiry).toUTCString()}${
                              n.timeAfterSavingExpiryTimeZone ? ` (${n.timeAfterSavingExpiryTimeZone})` : ''
                            }`
                          : 'N/A'
                      // UTC Expiry display
                      let utcExpiryDisplay =
                        n.launchTrigger === 'TIME_AFTER_SAVING' && n.timeAfterSavingExpiryInUTC
                          ? new Date(n.timeAfterSavingExpiryInUTC).toUTCString()
                          : 'N/A'
                      return (
                        <Table.Tr
                          key={n.id + n.status}
                          className={`border-t transition-colors ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          } hover:bg-blue-50`}
                        >
                          <Table.Td>{display(n.id)}</Table.Td>
                          <Table.Td className="font-medium">{display(n.heading)}</Table.Td>
                          <Table.Td>{display(n.subHeading)}</Table.Td>
                          <Table.Td>{display(launchTriggerDisplay)}</Table.Td>
                          <Table.Td>{display(expiryDisplay)}</Table.Td>
                          <Table.Td>{display(utcExpiryDisplay)}</Table.Td>
                          <Table.Td>
                            <span
                              className={`px-2 py-2 rounded-full text-md font-semibold ${
                                n.status === 'SCHEDULED'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : n.status === 'ACTIVE'
                                  ? 'bg-green-100 text-green-800'
                                  : n.status === 'EXPIRED'
                                  ? 'bg-gray-200 text-gray-500'
                                  : ''
                              }`}
                            >
                              {n.status === 'SCHEDULED'
                                ? 'SCHEDULED'
                                : n.status === 'ACTIVE'
                                ? 'ACTIVE'
                                : n.status === 'EXPIRED'
                                ? 'EXPIRED'
                                : display(n.status)}
                            </span>
                          </Table.Td>
                          <Table.Td className="text-right relative" style={{ overflow: 'visible' }}>
                            <Menu as="div" className="relative inline-block text-left">
                              <div>
                                <Menu.Button className="px-2 py-1 rounded border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-200 transition">
                                  &#x22EE;
                                </Menu.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                              >
                                <Menu.Items className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 ring-1 ring-black/5 focus:outline-none animate-fade-in z-50">
                                  {n.status === 'SCHEDULED' && (
                                    <>
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            type="button"
                                            className={`z-30 block w-full text-left px-5 py-2 text-gray-700 rounded transition ${
                                              active ? 'bg-blue-50 text-blue-700' : ''
                                            }`}
                                            onClick={() => {
                                              setEditNotification(n)
                                              setViewOnly(false)
                                              setShowScheduleModal(true)
                                            }}
                                          >
                                            Edit
                                          </button>
                                        )}
                                      </Menu.Item>
                                      <Menu.Item>
                                        {({ active }) => (
                                          <button
                                            type="button"
                                            className={`block w-full text-left px-5 py-2 text-red-600 rounded transition ${
                                              active ? 'bg-red-50 text-red-700' : ''
                                            }`}
                                            onClick={() => {
                                              setActionNotification(n)
                                              setConfirmAction('delete')
                                              setConfirmModalOpen(true)
                                            }}
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </Menu.Item>
                                    </>
                                  )}
                                  {n.status === 'ACTIVE' && (
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          type="button"
                                          className={`block w-full text-left px-5 py-2 text-yellow-700 rounded transition ${
                                            active ? 'bg-yellow-50 text-yellow-800' : ''
                                          }`}
                                          onClick={() => {
                                            setActionNotification(n)
                                            setConfirmAction('cancel')
                                            setConfirmModalOpen(true)
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      )}
                                    </Menu.Item>
                                  )}
                                  {n.status === 'EXPIRED' && (
                                    <Menu.Item>
                                      {({ active }) => (
                                        <button
                                          type="button"
                                          className={`block w-full text-left px-5 py-2 text-gray-700 rounded transition ${
                                            active ? 'bg-blue-50 text-blue-700' : ''
                                          }`}
                                          onClick={() => {
                                            setEditNotification(n)
                                            setViewOnly(true)
                                            setShowScheduleModal(true)
                                          }}
                                        >
                                          View
                                        </button>
                                      )}
                                    </Menu.Item>
                                  )}
                                </Menu.Items>
                              </Transition>
                            </Menu>
                          </Table.Td>
                        </Table.Tr>
                      )
                    })
                  )}
                </Table.Tbody>
              </Table>
            )}
          </div>
          <div
            className="flex items-center justify-between mt-4 text-sm text-gray-600 bg-white pt-2 z-10 sticky bottom-0 border-t border-gray-100"
            style={{ zIndex: 10 }}
          >
            <span>
              Showing {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <Pagination currentPage={page} totalCount={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </div>
        </div>
      </div>
      <CreateScheduleNotification
        open={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false)
          setEditNotification(null)
          setViewOnly(false)
        }}
        advertId={adId || ''}
        onSuccess={() => setRefreshFlag(f => f + 1)}
        editNotification={editNotification}
        viewOnly={viewOnly}
      />
      <Modal
        isOpen={confirmModalOpen}
        setIsOpen={setConfirmModalOpen}
        type="small"
        height="20vh"
        showCloseIcon={true}
        title={confirmAction === 'delete' ? 'Delete notification?' : 'Cancel notification?'}
      >
        <div className="mt-4 mb-6 text-gray-700 text-md">
          {confirmAction === 'delete'
            ? 'Are you sure you want to permanently delete this notification and all its data?'
            : 'Are you sure you want to expire this notification?'}
        </div>
        <div className="flex justify-center gap-4 mt-6">
          <button
            className="border border-blue-500 text-blue-600 px-8 py-2 rounded-lg font-semibold bg-white hover:bg-blue-50 transition"
            onClick={() => setConfirmModalOpen(false)}
            disabled={actionLoading}
          >
            No, Cancel
          </button>
          <button
            className="bg-red-600 text-white px-8 py-2 rounded-lg font-semibold hover:bg-red-700 transition relative"
            onClick={handleAction}
            disabled={actionLoading}
          >
            {confirmAction === 'delete' ? 'Yes, Delete' : 'Yes, Expire'}
          </button>
        </div>
        <div className="text-center text-xs text-red-500 mt-2">
          {confirmAction === 'delete' ? 'This action cannot be undone.' : 'This action will expire the notification.'}
        </div>
      </Modal>
    </div>
  )
}

export default PassNotifications
